import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useState, useEffect } from 'react';
import { X, DollarSign, Bell, User, Phone, Mail } from 'lucide-react';
import Modal from '../ui/Modal';

interface SessionData {
  id: number;
  booking_reference: string;
  booking_date: string;
  start_time: string;
  end_time: string;
  final_price: number | string;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  manual_booking_notes?: string | null;
}

interface EditManualSessionModalProps {
  session: SessionData | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    bookingId: number,
    data: {
      price?: number;
      notes?: string;
      customer_name?: string;
      customer_phone?: string;
      customer_email?: string;
      notify_customer: boolean;
    }
  ) => Promise<void>;
}

export default function EditManualSessionModal({
  session,
  isOpen,
  onClose,
  onConfirm,
}: EditManualSessionModalProps) {
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (session) {
      setPrice(String(parseFloat(String(session.final_price ?? 0))));
      setNotes(session.manual_booking_notes ?? '');
      setCustomerName(session.customer_name ?? '');
      setCustomerPhone(session.customer_phone ?? '');
      setCustomerEmail(session.customer_email ?? '');
      setNotifyCustomer(false);
      setError(null);
    }
  }, [session]);

  if (!session) return null;

  const origPrice       = String(parseFloat(String(session.final_price ?? 0)));
  const origNotes       = session.manual_booking_notes ?? '';
  const origName        = session.customer_name ?? '';
  const origPhone       = session.customer_phone ?? '';
  const origEmail       = session.customer_email ?? '';

  const priceChanged  = price !== origPrice;
  const hasChanges = () =>
    priceChanged ||
    notes        !== origNotes  ||
    customerName !== origName   ||
    customerPhone !== origPhone ||
    customerEmail !== origEmail;

  const handleSubmit = async () => {
    setError(null);

    if (!price || parseFloat(price) < 0) {
      setError('Price must be a positive number');
      return;
    }
    if (priceChanged && !notes.trim()) {
      setError('Notes are required when changing the price');
      return;
    }
    if (!hasChanges()) {
      onClose();
      return;
    }

    const payload: Parameters<typeof onConfirm>[1] = { notify_customer: notifyCustomer };
    if (priceChanged)               payload.price         = parseFloat(price);
    if (notes !== origNotes)        payload.notes         = notes;
    if (customerName !== origName)  payload.customer_name  = customerName;
    if (customerPhone !== origPhone) payload.customer_phone = customerPhone;
    if (customerEmail !== origEmail) payload.customer_email = customerEmail;

    setIsLoading(true);
    try {
      await onConfirm(session.id, payload);
      onClose();
    } catch (err: any) {
      // Extract the real backend message from the Axios error response
      const message =
        err.response?.data?.message ||
        err.message ||
        'Failed to update booking';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" animate={false}>
      {/* Header */}
      <div className="sticky top-0 bg-blue-50 border-b border-blue-100 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
        <div>
          <h2 className="text-lg font-bold text-blue-900">Edit Session</h2>
          <p className="text-xs text-blue-700">{session.booking_reference}</p>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
        >
          <X className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Body */}
      <div className="p-4 sm:p-6 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* ── Customer info ── */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Customer</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400 shrink-0" />
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Customer name"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-400 shrink-0" />
              <input
                type="text"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="Phone (optional)"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-400 shrink-0" />
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="Email (optional)"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>
        </div>

        <hr className="border-gray-100" />

        {/* ── Price ── */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1">
            <DollarSign className="w-4 h-4 text-blue-500" />
            Price ({CURRENCY_SYMBOL})
          </label>
          {/* onWheel blur prevents accidental scroll-to-change on number inputs */}
          <input
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </div>

        {/* ── Notes ── */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Notes
            {priceChanged
              ? <span className="text-red-500 ml-1">*</span>
              : <span className="text-gray-400 font-normal ml-1">(optional)</span>}
          </label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={priceChanged ? 'Required — explain the price change…' : 'Internal notes…'}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
          {priceChanged && (
            <p className="text-xs text-amber-600 mt-1">
              Notes are required when changing the price.
            </p>
          )}
        </div>

        {/* ── Notify customer ── */}
        {customerEmail && (
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={notifyCustomer}
              onChange={(e) => setNotifyCustomer(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-blue-600 cursor-pointer"
            />
            <span className="text-sm text-gray-700">
              <span className="font-semibold flex items-center gap-1">
                <Bell className="w-3.5 h-3.5 text-blue-500 inline" />
                Notify customer by email
              </span>
              <span className="block text-xs text-gray-500 mt-0.5">
                An update email will be sent to {customerEmail}
              </span>
            </span>
          </label>
        )}
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-3 flex gap-3 justify-end">
        <button
          onClick={onClose}
          disabled={isLoading}
          className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading || !hasChanges()}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </Modal>
  );
}
